import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, RefreshCw, Search, Pencil, Trash2,
  ChevronUp, ChevronDown, Tag, CheckCircle2, XCircle,
  Loader2,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { useDataRefresh } from '../../context/DataRefreshContext';
import { Category } from '../../types';
import CategoryFormModal from '../../components/admin/CategoryFormModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/admin/Toast';
import { useIsMobile } from '../../hooks/useIsMobile';
import '../../styles/AdminGlobal.css';
import './Admin.css';
import './CategoryManagement.css';
import '../../components/admin/MobileCardList.css';

// Status groups for order count computation
const STATUS_GROUPS: Record<string, string[]> = {
  future:       ['preorder'],
  not_response: ['pending', 'no_response'],
  confirmed:    ['processing', 'shipped', 'delivered'],
  cancelled:    ['cancelled'],
};

const CategoryManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState<string>('display_order');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { triggerRefresh } = useDataRefresh();
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false,
    message: '',
    type: 'success'
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);

      // Fetch categories and orders in parallel
      const [catResponse, ordersResponse] = await Promise.all([
        adminAPI.getCategories(),
        adminAPI.getOrders(),
      ]);

      // Parse categories
      let categoriesList: Category[] = [];
      if (catResponse.data) {
        if (Array.isArray(catResponse.data)) {
          categoriesList = catResponse.data;
        } else if (catResponse.data.results && Array.isArray(catResponse.data.results)) {
          categoriesList = catResponse.data.results;
        } else if (catResponse.data.data && Array.isArray(catResponse.data.data)) {
          categoriesList = catResponse.data.data;
        }
      } else if (Array.isArray(catResponse)) {
        categoriesList = catResponse as any;
      }

      // Parse orders
      let ordersArray: any[] = [];
      if (ordersResponse.data) {
        if (Array.isArray(ordersResponse.data)) {
          ordersArray = ordersResponse.data;
        } else if (ordersResponse.data.results && Array.isArray(ordersResponse.data.results)) {
          ordersArray = ordersResponse.data.results;
        }
      } else if (Array.isArray(ordersResponse)) {
        ordersArray = ordersResponse as any;
      }

      // Compute per-category order counts using STATUS_GROUPS
      // An order counts towards a category if ANY of its items belongs to that category
      const withCounts = categoriesList.map((cat: any) => {
        const counts: Record<string, number> = {};
        Object.entries(STATUS_GROUPS).forEach(([group, statuses]) => {
          counts[`orders_${group}`] = ordersArray.filter((order: any) => {
            const matchesStatus = statuses.includes((order.status || '').toLowerCase());
            if (!matchesStatus) return false;
            const items: any[] = order.items || [];
            return items.some((item: any) => item.product_category_id === cat.id);
          }).length;
        });
        return { ...cat, ...counts };
      });

      setCategories(withCounts);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setToast({ show: true, message: 'Failed to load categories', type: 'error' });
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (category: Category) => {
    setSelectedCategory(category);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;
    
    try {
      await adminAPI.deleteCategory(selectedCategory.id);
      setToast({ show: true, message: 'Category deleted successfully', type: 'success' });
      loadCategories();
      // ✅ Trigger refresh for category data
      triggerRefresh('categories');
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete category', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedCategory(null);
    }
  };

  const handleSave = async (formData: any) => {
    try {
      if (selectedCategory) {
        await adminAPI.updateCategory(selectedCategory.id, formData);
        setToast({ show: true, message: 'Category updated successfully', type: 'success' });
      } else {
        await adminAPI.createCategory(formData);
        setToast({ show: true, message: 'Category created successfully', type: 'success' });
      }
      setIsModalOpen(false);
      setSelectedCategory(null);
      loadCategories();
      // ✅ Trigger refresh for category data
      triggerRefresh('categories');
    } catch (error: any) {
      console.error('Save error:', error);
      console.error('Server response:', error?.response?.data);
      console.error('Status:', error?.response?.status);
      const serverDetail = error?.response?.data?.detail || error?.response?.data?.error || '';
      const serverType = error?.response?.data?.type || '';
      if (serverDetail) {
        setToast({ show: true, message: `Save failed: ${serverDetail} (${serverType})`, type: 'error' });
      }
      throw error;
    }
  };

  const handleAddNew = () => {
    setSelectedCategory(null);
    setIsModalOpen(true);
  };

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setCurrentPage(1);
  };

  const SortArrow = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ChevronUp size={10} className="cm-sort-arrow" />
      : <ChevronDown size={10} className="cm-sort-arrow" />;
  };

  // Filter + Sort
  const categoriesArray = useMemo(() => (Array.isArray(categories) ? categories : []), [categories]);
  const filteredCategories = useMemo(() => {
    let list = categoriesArray;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(c =>
        c.name_en?.toLowerCase().includes(q) ||
        c.name_bn?.toLowerCase().includes(q) ||
        c.slug?.toLowerCase().includes(q)
      );
    }
    if (statusFilter === 'active') list = list.filter(c => c.is_active !== false);
    else if (statusFilter === 'inactive') list = list.filter(c => c.is_active === false);

    list = [...list].sort((a: any, b: any) => {
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      if (typeof av === 'number' && typeof bv === 'number')
        return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return list;
  }, [categoriesArray, searchTerm, statusFilter, sortField, sortDir]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredCategories.length / pageSize));
  const paginated = filteredCategories.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageNums = (() => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); return pages; }
    pages.push(1);
    if (currentPage > 3) pages.push('...');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  })();

  return (
    <div className="admin-page cm-page">

      {/* ── TOP BAR ── */}
      <div className="cm-topbar">
        <h1 className="cm-page-title">Category Management</h1>
        <div className="cm-topbar-right">
          <button className="cm-btn cm-btn-add" onClick={handleAddNew}>
            <Plus size={13} /> Add Category <span className="cm-btn-badge">{categories.length}</span>
          </button>
          <button className="cm-btn cm-btn-refresh" onClick={loadCategories} disabled={loading}>
            {loading ? <span className="cm-spin"><Loader2 size={13} /></span> : <RefreshCw size={13} />} Refresh
          </button>
        </div>
      </div>

      {/* ── FILTER BAR ── */}
      <div className="cm-filter-wrap">
        <div className="cm-filter-left">
          <select
            className="cm-filter-select"
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <span className="cm-count"><strong>{filteredCategories.length}</strong> categories</span>
        </div>
        <div className="cm-filter-right">
          <div className="cm-search-wrap">
            <span className="cm-search-icon"><Search size={13} /></span>
            <input
              type="text"
              className="cm-search-input"
              placeholder="Search by name or slug…"
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
        </div>
      </div>

      {/* ── MOBILE CARD LIST ── */}
      {isMobile && (
        <div className="mobile-card-list">
          {loading && <div className="mc-loading">Loading categories…</div>}
          {!loading && filteredCategories.length === 0 && (
            <div className="mc-empty">No categories found.</div>
          )}
          {!loading && filteredCategories.map((cat: any) => (
            <div className="mobile-card" key={cat.id}>
              <div className="mc-top">
                {cat.image ? (
                  <img className="mc-thumb" src={cat.image} alt={cat.name_en} />
                ) : (
                  <div className="mc-thumb-placeholder"><Tag size={22} /></div>
                )}
                <div className="mc-info">
                  <div className="mc-title">{cat.name_bn || cat.name_en}</div>
                  <div className="mc-subtitle">{cat.name_en}</div>
                </div>
                <div className="mc-badge-corner">
                  <span className={`mc-active-pill ${cat.is_active !== false ? 'active' : 'inactive'}`}>
                    {cat.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="mc-divider" />
              <div className="mc-count-row">
                {(cat.orders_future ?? 0) > 0 && (
                  <span className="mc-count-pill mc-count-future">Future: {cat.orders_future}</span>
                )}
                {(cat.orders_not_response ?? 0) > 0 && (
                  <span className="mc-count-pill mc-count-not-resp">No Resp: {cat.orders_not_response}</span>
                )}
                {(cat.orders_confirmed ?? 0) > 0 && (
                  <span className="mc-count-pill mc-count-confirmed">Confirmed: {cat.orders_confirmed}</span>
                )}
                {(cat.orders_cancelled ?? 0) > 0 && (
                  <span className="mc-count-pill mc-count-cancelled">Cancelled: {cat.orders_cancelled}</span>
                )}
              </div>
              <div className="mc-actions">
                <button className="mc-btn mc-btn-edit" onClick={() => handleEdit(cat as Category)}>
                  <Pencil size={12} /> Edit
                </button>
                <button className="mc-btn mc-btn-delete" onClick={() => handleDeleteClick(cat as Category)}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── DESKTOP TABLE ── */}
      {!isMobile && (
        <div className="cm-table-card">
          <div className="cm-table-scroll">
            <table className="cm-table">
              <colgroup>
                <col className="cm-col-img" />
                <col className="cm-col-name" />
                <col className="cm-col-slug" />
                <col className="cm-col-order" />
                <col className="cm-col-status" />
                <col className="cm-col-future" />
                <col className="cm-col-notresp" />
                <col className="cm-col-conf" />
                <col className="cm-col-cancel" />
                <col className="cm-col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th className="cm-th">Image</th>
                  <th className="cm-th cm-th-sort" onClick={() => handleSort('name_en')}>
                    Category <SortArrow field="name_en" />
                  </th>
                  <th className="cm-th cm-th-sort" onClick={() => handleSort('slug')}>
                    Slug <SortArrow field="slug" />
                  </th>
                  <th className="cm-th cm-th-sort" onClick={() => handleSort('display_order')}>
                    Order <SortArrow field="display_order" />
                  </th>
                  <th className="cm-th cm-th-sort" onClick={() => handleSort('is_active')}>
                    Status <SortArrow field="is_active" />
                  </th>
                  <th className="cm-th cm-th-sort" onClick={() => handleSort('orders_future')}>
                    Future <SortArrow field="orders_future" />
                  </th>
                  <th className="cm-th cm-th-sort" onClick={() => handleSort('orders_not_response')}>
                    Not Responded <SortArrow field="orders_not_response" />
                  </th>
                  <th className="cm-th cm-th-sort" onClick={() => handleSort('orders_confirmed')}>
                    Confirmed <SortArrow field="orders_confirmed" />
                  </th>
                  <th className="cm-th cm-th-sort" onClick={() => handleSort('orders_cancelled')}>
                    Cancelled <SortArrow field="orders_cancelled" />
                  </th>
                  <th className="cm-th" style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={10} className="cm-loading">
                      <span className="cm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>
                      Loading categories…
                    </td>
                  </tr>
                )}
                {!loading && paginated.length === 0 && (
                  <tr>
                    <td colSpan={10} className="cm-empty">No categories found.</td>
                  </tr>
                )}
                {!loading && paginated.map((cat: any) => {
                  const isActive = cat.is_active !== false;
                  const future = cat.orders_future ?? 0;
                  const notResp = cat.orders_not_response ?? 0;
                  const confirmed = cat.orders_confirmed ?? 0;
                  const cancelled = cat.orders_cancelled ?? 0;

                  return (
                    <tr className="cm-row" key={cat.id}>
                      {/* Image */}
                      <td className="cm-td">
                        {cat.image ? (
                          <img src={cat.image} alt={cat.name_en} className="cm-img" />
                        ) : (
                          <div className="cm-img-placeholder"><Tag size={18} /></div>
                        )}
                      </td>

                      {/* Name */}
                      <td className="cm-td">
                        <span className="cm-cat-name">{cat.name_bn || cat.name_en}</span>
                        <span className="cm-cat-name-sub">{cat.name_en}</span>
                      </td>

                      {/* Slug */}
                      <td className="cm-td">
                        <span className="cm-slug-text">{cat.slug || '—'}</span>
                      </td>

                      {/* Display Order */}
                      <td className="cm-td" style={{ textAlign: 'center' }}>
                        <span className="cm-order-text">{cat.display_order ?? 0}</span>
                      </td>

                      {/* Status */}
                      <td className="cm-td">
                        <span className={`cm-status-chip ${isActive ? 'cm-status-active' : 'cm-status-inactive'}`}>
                          {isActive
                            ? <><CheckCircle2 size={9} /> Active</>
                            : <><XCircle size={9} /> Inactive</>
                          }
                        </span>
                      </td>

                      {/* Future */}
                      <td className="cm-td" style={{ textAlign: 'center' }}>
                        <span className={`cm-count-badge ${future > 0 ? 'cm-badge-future' : 'cm-badge-future-zero'}`}>
                          {future}
                        </span>
                      </td>

                      {/* Not Responded */}
                      <td className="cm-td" style={{ textAlign: 'center' }}>
                        <span className={`cm-count-badge ${notResp > 0 ? 'cm-badge-notresp' : 'cm-badge-notresp-zero'}`}>
                          {notResp}
                        </span>
                      </td>

                      {/* Confirmed */}
                      <td className="cm-td" style={{ textAlign: 'center' }}>
                        <span className={`cm-count-badge ${confirmed > 0 ? 'cm-badge-conf' : 'cm-badge-conf-zero'}`}>
                          {confirmed}
                        </span>
                      </td>

                      {/* Cancelled */}
                      <td className="cm-td" style={{ textAlign: 'center' }}>
                        <span className={`cm-count-badge ${cancelled > 0 ? 'cm-badge-cancel' : 'cm-badge-cancel-zero'}`}>
                          {cancelled}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="cm-td" style={{ padding: '6px 4px' }}>
                        <div className="cm-actions-row">
                          <button
                            className="cm-action-btn cm-action-edit"
                            onClick={() => handleEdit(cat as Category)}
                            title="Edit Category"
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            className="cm-action-btn cm-action-del"
                            onClick={() => handleDeleteClick(cat as Category)}
                            title="Delete Category"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── PAGINATION ── */}
          <div className="cm-pagination">
            <div className="cm-page-size-selector">
              <span>Show</span>
              <select
                className="cm-page-size-select"
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              >
                {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>per page</span>
            </div>

            <div className="cm-page-info">
              {filteredCategories.length === 0 ? '0 categories' :
                `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, filteredCategories.length)} of ${filteredCategories.length} categories`}
            </div>

            <div className="cm-page-controls">
              <button
                className="cm-page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              >‹</button>
              <div className="cm-page-numbers">
                {pageNums.map((n, i) =>
                  n === '...'
                    ? <span key={`e${i}`} style={{ padding: '0 4px', color: '#94a3b8' }}>…</span>
                    : (
                      <button
                        key={n}
                        className={`cm-page-num${currentPage === n ? ' active' : ''}`}
                        onClick={() => setCurrentPage(n as number)}
                      >{n}</button>
                    )
                )}
              </div>
              <button
                className="cm-page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              >›</button>
            </div>
          </div>
        </div>
      )}

      <CategoryFormModal
        isOpen={isModalOpen}
        onClose={() => { 
          setIsModalOpen(false); 
          setSelectedCategory(null); 
        }}
        onSave={handleSave}
        category={selectedCategory}
        categories={categories}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedCategory(null);
        }}
        onConfirm={handleDelete}
        title="Delete Category"
        message={`Are you sure you want to delete "${selectedCategory?.name_en}"? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
      />

      {toast.show && (
        <Toast
          {...toast}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default CategoryManagement;
